import { ColoredChip } from '@components/colored-elements';
import { EntityNameColumn } from '@components/entity-name-column';
import { TimeAgo } from '@components/time-ago';
import { useDefaultImage } from '@lib/common';
import { Avatar, ListItem, ListItemAvatar, ListItemText, Tooltip, Typography } from '@material-ui/core';
import { blue, deepOrange, teal } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import CopyClipboard from '@material-ui/icons/AttachFile';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { camelCase, startCase, uniq, uniqBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { $eq } from '../filters';
import { QueryParamsAdapter } from '@lib/common';
type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];
// import { Link } from 'react-router-dom';
import { windowObj } from '@lib/common';
import { Link } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    small: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    orange: {
      color: theme.palette.getContrastText(deepOrange[500]),
      backgroundColor: deepOrange[500],
    },
    containedImage: {
      '& .MuiAvatar-img': {
        objectFit: 'contain',
      },
    },
  })
);
const ContainedAvatar = withStyles({
  img: {
    objectFit: 'contain',
  },
})(Avatar);
/**
 * Convert a string based enum into objects
 * and redable labels.
 *
 * @param enum_ The enum to convert
 */
export const createEnumMap = (enum_: Record<string, any>, retainSpecialChars = false): EnumMap =>
  Object.values(enum_).map(value => ({
    label: retainSpecialChars ? value : startCase(camelCase(value)),
    value,
  }));

/**
 * Render a enum map to Select options.
 * @param enumMap The Enum map
 */
export const EnumsToOptions = (enumMap: EnumMap) => (
  <>
    {enumMap.map(v => (
      <option key={v.value} value={v.value}>
        {v.label}
      </option>
    ))}
  </>
);

/**
 * Override equals operator to show readable
 * values of enums.
 */
export const enumOperatorOverride = {
  [$eq.key]: {
    ChipLabel({ label, value }) {
      return (
        <>
          {label} is {startCase(camelCase(value))}
        </>
      );
    },
  },
};

/**
 * Higher order function to create c
 * @param entity The entity to create object for.
 */
export const cellForEntity =
  (entity: 1 | 2 | '1' | '2') =>
  ({ row }) =>
    (row.original[`entity${entity}`] || row.original[`entity${entity}_vtr_id`]) && (
      <EntityNameColumn
        name={row.original[`entity${entity}`]}
        type={row.original[`entity${entity}_type`]}
        secondary={
          row.original[`entity${entity}_vtr_id`] ? (
            <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
          ) : null
        }
      />
    );

/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForContent = ({ row }) =>
  row.original.content && (
    <EntityNameColumn
      name={row.original.content}
      type="CONTENT"
      secondary={
        row.original.content_vtr_id ? (
          <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
        ) : null
      }
    />
  );

export const QuickTitle = ({ title, subTitle = 'Advanced Filters' }) => (
  <>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="body2" color="textSecondary">
      {subTitle}
    </Typography>
    <br />
  </>
);

export const addTitleToFilter =
  (Filter: any, title?: string, subTitle = 'Advanced Filters') =>
  props => {
    const actualTitle = useMemo(
      () => title || props.column.render('Header'),
      [props.column.Header]
    );
    return (
      <>
        <QuickTitle title={actualTitle} subTitle={subTitle} />
        <Filter {...props} />
      </>
    );
  };

export const CellForCompanyTypes = ({ row }) => {
  const max_limit: number = 2;
  const list_of_company_types = row.original.company_types
    ? uniqBy(row.original.company_types, 'name')
    : [];

  const title = list_of_company_types
    .slice(max_limit)
    .map(eachCompanyType => eachCompanyType['name'])
    .join(', ');
  const sliced = list_of_company_types.slice(0, max_limit);
  return (
    row.original.company_types && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachCompanyType, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachCompanyType['name']}>
                <ColoredChip
                  colored={blue[500]}
                  size="small"
                  // variant="outlined"
                  label={eachCompanyType['name']}
                />
              </Tooltip>
              {index === sliced.length - 1 && list_of_company_types.length - max_limit > 0 ? (
                <>
                  &nbsp;
                  <Tooltip title={title}>
                    <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                      list_of_company_types.length - max_limit
                    }`}</span>
                  </Tooltip>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};

export const CellForDealContentCount = (row, count_param) => {
  return (
    row.original.deal_content_count && (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {count_param === 'deal_count' ? (
          <Tooltip title={`Deals: ${row.original.deal_content_count.deal_count}`}>
            {/* <Chip
              color="primary"
              size="small"
              variant="outlined"
              component={Link}
              target="_blank"
              to={`${window.location.origin}/deals/search/?filters[0].id=status&filters[0].value.$in=approved%2Cskipped&filters[1].id=entity_id&filters[1].value.$eq=${row.id}`}
              label={`${row.original.deal_content_count.deal_count}`}
            /> */}

            <Link
              href={`${window.location.origin}/deals/search/?filters[0].id=status&filters[0].value.$in=approved%2Cskipped&filters[1].id=entity_id&filters[1].value.$eq=${row.id}`}
              target="_blank">
              <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}} color="primary" variant="h6">
                {row.original.deal_content_count.deal_count}
              </Typography>
            </Link>
          </Tooltip>
        ) : (
          // <Tooltip title={`Content: ${row.original.deal_content_count.content_count}`}>
          //   <ColoredChip
          //     colored={blue[500]}
          //     size="small"
          //     variant="outlined"
          //     component={Link}
          //     to={`/company-content/search?filters[0].id=company_name&filters[0].value.$eq=${row.original.vtr_company_name}&filters[1].id=content_title&filters[1].value.$empty=false&filters[2].id=is_publish&filters[2].value.$in=1%2C2`}
          //     label={`${row.original.deal_content_count.content_count}`}
          //   />
          // </Tooltip>

          <Link
            href={`${window.location.origin}/company-content/search?filters[0].id=company_vtr_id_eq&filters[0].value.$eq=${row.original.vtr_id}&filters[1].id=content_title&filters[1].value.$empty=false&filters[2].id=is_publish&filters[2].value.$in=1%2C2`}
            target="_blank">
            <Typography color="secondary" variant="h6">
              {row.original.deal_content_count.content_count}
            </Typography>
          </Link>
        )}
      </div>
    )
  );
};

export const CellForServiceCount = (row, count_param) => {
  return (
    row.original.deal_content_count && (
      <div style={{ display: 'flex', flexDirection: 'row'}}>
        <Tooltip style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}} title={`No.of services: ${row.original.number_of_services}`}>
          <Typography color="primary" variant="h6">
            {row.original.number_of_services}
          </Typography>
        </Tooltip>
      </div>
    )
  );
};

export const CellForPersonCount = ({ row }) => {
  const count = row.original.c2p_count;
  const vtrId = row.original.vtr_id;

  if (count === undefined || count === null) {
    return null;
  }

  const filters = [{
    id: 'vtr_id',
    value: {
      $eq: vtrId
    }
  },{
    id: 'currently_working',
    value: {
      $notlike: 'no'
    }}];

  const search = QueryParamsAdapter.toParams({ filters }).toString();
  const href = `${window.location.origin}/company-person/search?${search}`;
  
  return (
    <Tooltip title={`#person attached: ${count}`}>
      <Link
        href={href}
        target="_blank">
        <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red' : '' }} color="primary" variant="h6">
          {count}
        </Typography>
      </Link>
    </Tooltip>
  );
};

export const CellForClientCount = (row, count_param) => {
  return (
    row.original.deal_content_count && (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Link
          href={`${window.location.origin}/company-content/search?filters[0].id=company_name&filters[0].value.$eq=${row.original.vtr_company_name}&filters[1].id=vtr_id&filters[1].value.$eq=${row.original.vtr_id}&filters[2].id=client_name&filters[2].value.$empty=false`}
          target="_blank">
          <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}} color="secondary" variant="h6">
            {row.original.clients_count}
          </Typography>
        </Link>
      </div>
    )
  );
};

export const cellForOfficeLocations = ({ row }) => {
  const max_limit: number = 2;
  const listOfOtherOfficeCountries =
    row.original.other_office_locations &&
    row.original.other_office_locations.replaceAll(',', '').replaceAll('|', '').trim()
      ? uniq(
          row.original.other_office_locations
            .split('|')
            .map(x => x.split(','))
            .map(x => x[x.length - 1].trim())
        )
      : [];

  const title = listOfOtherOfficeCountries.slice(max_limit).join(', ');
  const sliced = listOfOtherOfficeCountries.slice(0, max_limit);

  return (
    row.original.other_office_locations && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachOtherOfficeCountry, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachOtherOfficeCountry}>
                <ColoredChip
                  colored={teal[500]}
                  size="small"
                  // variant="outlined"
                  label={eachOtherOfficeCountry}
                />
              </Tooltip>
              {index === sliced.length - 1 && listOfOtherOfficeCountries.length - max_limit > 0 ? (
                <Tooltip title={title}>
                  <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                    listOfOtherOfficeCountries.length - max_limit
                  }`}</span>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};
export const CellForGroupName = ({ row }) => {
  // return row?.original?.primary_company_name
  return <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}}>{row?.original?.primary_company_name ? row.original.primary_company_name : 'NA'}</Typography>

  // return (row?.original?.company_types?.length) > 0 ? row.original.vtr_company_name : row.original.group_company_name
};
export const CellForCompanyName = ({ value, row }) => {
  const classes = useStyles();
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_URL}/${row.original.vtr_id}.png`,
      row.original.logo_url ? `${windowObj.RAZZLE_TMDB_IMAGE_URL}${row.original.logo_url}` : '',
      '/images/company_default.svg',
    ].filter(e => e);
  }, []);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/company_default.svg',
    useSuspense: false,
  });
  return (
    value && (
      <Tooltip title={''}>
        <ListItem style={{ padding: 0 }} component="div">
          <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
            <Avatar
              alt={`${row.original.vtr_company_name}`}
              src={fallbackedSource}
              className={classes.containedImage}
            />
          </ListItemAvatar>
          <ListItemText
            primary={
              
              <Typography variant="body2" style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}}>
                <ClipBoard
                  parentValue={value}
                  icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
                  value={row.original.vtr_id}
                />
              </Typography>
            }
          />
        </ListItem>
      </Tooltip>
    )
  );
};

export const CommonCellForCompanyLogo = ({ vtr_id, name}) => {
  const classes = useStyles();
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_URL}/${vtr_id}.png`,
      '/images/company_default.svg',
    ].filter(e => e);
  }, [vtr_id]);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/company_default.svg',
    useSuspense: false,
  });
  
  return (
    vtr_id && (
      <Tooltip title={''}>
        <ListItem style={{ padding: 0 }} component="div">
          <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
            <Avatar
              alt={`${name}`}
              src={fallbackedSource}
              className={classes.containedImage}
            />
          </ListItemAvatar>
          <ListItemText
            primary={
              
              <Typography variant="body2" >
                <ClipBoard
                  parentValue={name}
                  icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
                  value={vtr_id}
                />
              </Typography>
            }
          />
        </ListItem>
      </Tooltip>
    )
  );
};

export const CellForCompanyAka = ({ value, row }) => {
  return <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}}>{!!row.original.company_aka && row.original.company_aka}</Typography>
};
export const CellForLastModifiedAt = ({ value, row }) => (
  <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />
);

export const CellForHQCountry = ({ value, row}) => {
  return (
    <Typography style={{ color: (row.original.company_active == 'Inactive') ? 'red'  : ''}}>{value}</Typography>
  );
};

export default function ClipBoard({ parentValue, icon, display = 'block', value }) {
  const [isHovering, setHovering] = useState(false);
  const [isClick, setClick] = useState(false);
  return (
    <>
      <span
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => {
          setClick(false);
          setTimeout(() => {
            setHovering(false);
          }, 5000);
        }}>
        {parentValue}
      </span>
      <span
        style={{
          color: 'inherit',
          fontSize: 11,
          display: isHovering ? 'inline' : 'none',
          alignItems: 'center',
          paddingTop: '2px',
        }}>
        <Tooltip
          title={isClick ? 'Copied!!' : 'Copy the Vitrina Id'}
          // arrow
          // followCursor
          onClick={() => {
            navigator.clipboard.writeText(value);
            setClick(true);
          }}>
          {icon}
        </Tooltip>
      </span>
    </>
  );
}
